import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Briefcase, Building2, KeyRound } from 'lucide-react';
import { apiService } from '../../services/api';
import ProfileImageUpload from '../common/ProfileImageUpload';
import config from '../../config';
import '../../styles/create-mentor-modal.scss';

interface EditUserModalProps {
  user: any;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [profileImage, setProfileImage] = useState<File | null>(null);

  // Construct full image URL for display using env variable
  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    // Use API base URL without /api suffix to get backend root URL
    const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${imagePath}`;
  };

  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    username: (user as any).username || '',
    phone: user.phone || '',
    bio: user.bio || '',
    status: user.status || 'active',
    // Contraseña opcional: si se llena, el admin la resetea (mín. 6). En blanco = no cambia.
    password: '',
    // Campos específicos de rol
    title: user.title || '',
    company: user.company || '',
    current_profile_image: getImageUrl(user.profile_image),
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageSelected = (file: File) => {
    console.log('EditUserModal - Image selected:', file);
    setProfileImage(file);
  };

  const handleImageRemoved = () => {
    setProfileImage(null);
    setFormData(prev => ({ ...prev, current_profile_image: null }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name) newErrors.first_name = 'Nombre es requerido';
    if (!formData.last_name) newErrors.last_name = 'Apellido es requerido';
    if (!formData.email) newErrors.email = 'Email es requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email inválido';
    if (formData.password && formData.password.length < 6) newErrors.password = 'Mínimo 6 caracteres';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    console.log('EditUserModal - Submitting with profileImage:', profileImage);

    try {
      setLoading(true);

      // Si hay imagen nueva, usar FormData con POST, sino usar JSON con PUT
      if (profileImage) {
        console.log('EditUserModal - Using FormData with image via POST');
        const submitData = new FormData();
        submitData.append('first_name', formData.first_name);
        submitData.append('last_name', formData.last_name);
        submitData.append('email', formData.email);
        if (formData.username) submitData.append('username', formData.username);

        if (formData.phone) submitData.append('phone', formData.phone);
        if (formData.bio) submitData.append('bio', formData.bio);
        if (formData.status) submitData.append('status', formData.status);
        if (formData.title) submitData.append('title', formData.title);
        if (formData.company) submitData.append('company', formData.company);
        if (formData.password) submitData.append('password', formData.password);

        submitData.append('profile_image', profileImage);

        // Usar POST porque PHP/CodeIgniter solo parsea FormData en POST, no en PUT
        await apiService.post(`/users/${user.id}`, submitData);
      } else {
        console.log('EditUserModal - Using JSON without image');
        // Sin imagen, enviar JSON normal
        const jsonData: any = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          username: formData.username || undefined,
          status: formData.status,
        };

        if (formData.phone) jsonData.phone = formData.phone;
        if (formData.bio) jsonData.bio = formData.bio;
        if (formData.title) jsonData.title = formData.title;
        if (formData.company) jsonData.company = formData.company;
        if (formData.password) jsonData.password = formData.password;

        await apiService.put(`/users/${user.id}`, jsonData);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating user:', error);
      setErrors({
        submit: error.response?.data?.message || 'Error al actualizar usuario. Intenta nuevamente.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-mentor-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <div className="modal-header__content">
            <User size={24} className="header-icon" />
            <div>
              <h2>Editar Usuario</h2>
              <p className="modal-subtitle">Actualizar información de {user.first_name} {user.last_name}</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div className="modal-body">
            {errors.submit && (
              <div className="alert alert--danger">
                {errors.submit}
              </div>
            )}

            {/* Profile Image */}
            <div className="form-section">
              <h3 className="form-section__title">Foto de Perfil</h3>
              <ProfileImageUpload
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                currentImage={formData.current_profile_image || undefined}
                size="lg"
                maxSize={5}
              />
            </div>

            {/* Personal Information */}
            <div className="form-section">
              <h3 className="form-section__title">Información Personal</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">
                    <User size={16} /> Nombre *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={errors.first_name ? 'error' : ''}
                  />
                  {errors.first_name && <span className="error-text">{errors.first_name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">
                    <User size={16} /> Apellido *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={errors.last_name ? 'error' : ''}
                  />
                  {errors.last_name && <span className="error-text">{errors.last_name}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">
                    <Mail size={16} /> Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="username">
                    <User size={16} /> Usuario (con esto inicia sesión)
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    autoCapitalize="none"
                    spellCheck={false}
                    className={errors.username ? 'error' : ''}
                  />
                  {errors.username && <span className="error-text">{errors.username}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">
                    <Phone size={16} /> Teléfono
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="bio">Biografía</label>
                <textarea
                  id="bio"
                  name="bio"
                  rows={3}
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Breve descripción profesional..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Estado</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="pending_approval">Pendiente de Aprobación</option>
                </select>
              </div>
            </div>

            {/* Contraseña (opcional) */}
            <div className="form-section">
              <h3 className="form-section__title">Contraseña</h3>
              <div className="form-group">
                <label htmlFor="password">
                  <KeyRound size={16} /> Nueva contraseña
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  placeholder="Dejar en blanco para no cambiarla"
                  className={errors.password ? 'error' : ''}
                />
                {errors.password && <span className="error-text">{errors.password}</span>}
                <span className="error-text" style={{ color: '#8a8378' }}>Mínimo 6 caracteres. Solo se actualiza si escribes algo.</span>
              </div>
            </div>

            {/* Professional Information - Only if mentor or has company/title */}
            {(user.role_name === 'mentor' || formData.title || formData.company) && (
              <div className="form-section">
                <h3 className="form-section__title">Información Profesional</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="title">
                      <Briefcase size={16} /> Título/Cargo
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Ej: Senior Software Engineer"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">
                      <Building2 size={16} /> Empresa
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-section" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <p style={{ fontSize: '14px', color: '#666', marginTop: 0 }}>
                <strong>Rol:</strong> {user.role_name} · <strong>ID:</strong> {user.id}
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
